import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'
import partition from 'lodash/partition'
import type { $Values } from 'utility-types'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import { ExplotableList } from 'modules/core/components/Explotable'
import InlineSearch from 'modules/core/components/InlineSearch'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Loading from 'modules/core/components/Loading'
import ScrollableList from 'modules/core/components/ScrollableList'
import { useMemo } from 'modules/core/hooks/useMemo'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useTriggers } from 'modules/automations/components/NewAutomationModal/hooks'
import WeeklySelect from 'modules/automations/components/blocks/WeeklySelect'
import { Trigger } from 'modules/automations/store/models/Trigger'

import Category from './Category'

type CategoryProps = React.ComponentProps<typeof Category>

type ContentKind = $Values<typeof Trigger.ContentKinds>
interface Props {
  content: ContentKind
  selected: Trigger | null | undefined
  setSelected: CategoryProps['setSelected']
  field: Field
  selectedWeekday: number | undefined
  setSelectedWeekday: (weekday: number) => void
}

const Content = ({
  content,
  selected,
  setSelected,
  field,
  selectedWeekday,
  setSelectedWeekday,
}: Props) => {
  const [fetchingTriggers, triggers, search, setSearch] = useTriggers()

  const repeatFilter = partition(triggers, (trigger) => trigger.isRepeat)

  const beforeAfterFilter = partition(repeatFilter[1], function (trigger) {
    return trigger.isBeforeAfter
  })

  const pickedDayTranslation = useMemo(() => {
    if (content === Trigger.ContentKinds.monthly) {
      return i18n.t('automations.new.subtitle_monthly_trigger', {
        day: stringToDate(field.value, 'ui').date(),
      })
    } else if (content === Trigger.ContentKinds.trimester) {
      return i18n.t('automations.new.subtitle_trimester_trigger', {
        day: stringToDate(field.value, 'ui').date(),
        month: stringToDate(field.value, 'ui').format('MMMM'),
      })
    }

    return i18n.t('automations.new.subtitle_yearly_trigger', {
      day: stringToDate(field.value, 'ui').date(),
      month: stringToDate(field.value, 'ui').format('MMMM'),
    })
  }, [field.value]) // eslint-disable-line react-hooks/exhaustive-deps

  if (
    [
      Trigger.ContentKinds.monthly,
      Trigger.ContentKinds.trimester,
      Trigger.ContentKinds.yearly,
    ].includes(content)
  ) {
    return (
      <>
        <DayPicker label={i18n.t('automations.actions.time.monthly_label')} field={field} />
        {field.value && (
          <Box width="full" textAlign="left">
            {pickedDayTranslation}
          </Box>
        )}
      </>
    )
  }

  if (content === Trigger.ContentKinds.weekly) {
    return (
      <WeeklySelect setSelectedWeekday={setSelectedWeekday} selectedWeekday={selectedWeekday} />
    )
  }

  if (fetchingTriggers) return <Loading />

  const categories = [
    {
      label: i18n.t('automations.actions.categories.event'),
      triggers: beforeAfterFilter[1],
    },
    {
      label: i18n.t('automations.actions.categories.before_after_event'),
      triggers: beforeAfterFilter[0],
    },
    {
      label: i18n.t('automations.actions.categories.repeat'),
      triggers: repeatFilter[0],
    },
  ]

  return (
    <BoxList>
      <InlineSearch autofocus onSearch={(ev) => setSearch(ev.target.value)} search={search} />
      <ScrollableList size="medium">
        <ExplotableList>
          {categories.map((category, index) => (
            <Category
              key={index}
              category={category}
              selected={selected}
              setSelected={setSelected}
            />
          ))}
        </ExplotableList>
      </ScrollableList>
    </BoxList>
  )
}

export default observer(Content)
